/**
 * @fileoverview Client details query hook.
 * @description Wraps GET /sales/clients/{tenantId} in a TanStack query, idle until there is a client
 * to show. The data carries the `tp1.` ETag that Edit sends back as `If-Match`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import { getClientDetails } from '@/features/sales/services/sales-clients.service'

/**
 * @description Fetches one client in full, with its ETag.
 * @param {string | null} tenantId - The client to fetch, or null to stay idle.
 * @returns {ReturnType<typeof useQuery>} The query (data is the client and its ETag).
 */
export function useClientDetails(tenantId: string | null) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.client(tenantId ?? ''),
    queryFn: () => getClientDetails(tenantId ?? ''),
    enabled: tenantId !== null,
  })
}
